/**
 * Moteur i18n.
 *
 * ## Pourquoi pas i18next
 *
 * L'application compte deux langues et une cinquantaine de chaînes. i18next apporterait
 * le chargement paresseux, la détection via greffons et la pluralisation ICU — dont rien
 * n'est nécessaire ici — au prix d'une quarantaine de kilo-octets et d'une surface d'API
 * bien plus large que le besoin. Le contrôle de type, qui est ce qui compte vraiment, est
 * ici **meilleur** : le catalogue est vérifié à la compilation, ce que i18next ne fait pas
 * sans génération de types.
 *
 * ## Traduire au bon moment
 *
 * Les couches métier (`session.ts`, `peer.ts`, `browser.ts`) ne produisent **jamais** de
 * texte formaté : elles émettent un `MessageDescriptor` — une clé et ses paramètres.
 * La mise en forme n'a lieu qu'au moment de l'affichage.
 *
 * Ce n'est pas une coquetterie d'architecture. Formater trop tôt produit un bug précis :
 * un message d'erreur généré en français reste en français après un passage à l'anglais,
 * parce que la chaîne est déjà cuite. C'est exactement le défaut que ce découpage évite.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { LOCALES, catalogs, type Locale, type MessageKey, type PluralBase } from './messages.js';

// Ré-exportés pour que les consommateurs n'aient qu'un seul point d'import à connaître.
export { LOCALES, LOCALE_LABELS } from './messages.js';
export type { Locale, MessageKey, PluralBase } from './messages.js';

const STORAGE_KEY = 'attrape:locale';

// ─────────────────────────────────────────────────────────────────────────────
// Locale
// ─────────────────────────────────────────────────────────────────────────────

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Sommes-nous dans un navigateur ?
 *
 * Ce module doit rester chargeable hors navigateur (tests unitaires). Tester `window`
 * plutôt que de tenter l'accès et de rattraper l'exception évite aussi un avertissement
 * expérimental de Node, qui implémente un `localStorage` inopérant.
 */
function hasBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Détermine la langue de départ : préférence enregistrée, puis langue du navigateur,
 * puis anglais.
 *
 * Toutes les accès sont protégés : ce module doit rester chargeable hors navigateur
 * (tests unitaires), où `localStorage` et `navigator` n'existent pas.
 */
export function detectLocale(): Locale {
  if (hasBrowserStorage()) {
    try {
      const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (isLocale(stored)) return stored;
    } catch {
      // Stockage refusé (navigation privée, politique) : on poursuit la détection.
    }
  }

  const language = globalThis.navigator?.language ?? '';
  if (language.toLowerCase().startsWith('fr')) return 'fr';
  return 'en';
}

interface LocaleStore {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleStore>((set) => ({
  locale: detectLocale(),
  setLocale: (locale) => {
    if (hasBrowserStorage()) {
      try {
        globalThis.localStorage?.setItem(STORAGE_KEY, locale);
      } catch {
        // Préférence non conservée : sans conséquence, la détection reprendra la main.
      }
      // L'attribut `lang` conditionne la césure, la synthèse vocale et la traduction
      // automatique du navigateur : il doit suivre la langue affichée.
      document.documentElement.lang = locale;
    }
    set({ locale });
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Traduction
// ─────────────────────────────────────────────────────────────────────────────

export type MessageParams = Record<string, string | number>;

/**
 * Remplace les jetons `{nom}` par leur valeur.
 *
 * Un jeton sans correspondance est laissé tel quel plutôt que remplacé par « undefined » :
 * en cas d'oubli, mieux vaut voir `{size}` à l'écran et comprendre immédiatement, qu'un
 * texte qui semble correct mais qui ment.
 */
export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (token, name: string) => {
    const value = params[name];
    return value === undefined ? token : String(value);
  });
}

export interface Translator {
  t: (key: MessageKey, params?: MessageParams) => string;
  plural: (base: PluralBase, count: number, params?: MessageParams) => string;
}

export function createTranslator(locale: Locale): Translator {
  const catalog = catalogs[locale];

  const t = (key: MessageKey, params?: MessageParams): string => {
    // Repli sur le français si l'anglais manquait une clé. Le type l'interdit, mais un
    // catalogue partiellement chargé ne doit jamais afficher une clé technique.
    const template = catalog[key] ?? catalogs.fr[key];
    return interpolate(template, params);
  };

  return {
    t,
    plural: (base, count, params) => {
      const suffix = count === 1 ? 'one' : 'other';
      return t(`${base}.${suffix}` as MessageKey, { count, ...params });
    },
  };
}

/**
 * Traducteur lié à la langue courante. Le composant se réaffiche au changement de langue.
 */
export function useT(): Translator {
  const locale = useLocaleStore((state) => state.locale);
  return useMemo(() => createTranslator(locale), [locale]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Mise en forme sensible à la langue
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Unités d'octets par langue.
 *
 * Le français utilise des préfixes binaires abrégés (Ko, Mo), l'anglais des préfixes
 * décimaux courants (KB, MB). Afficher « 1,2 Mo » à un utilisateur anglophone n'est pas
 * seulement inélégant : le séparateur décimal virgule se lit mal, et l'unité ne lui parle pas.
 */
const BYTE_UNITS: Record<Locale, readonly string[]> = {
  fr: ['o', 'Ko', 'Mo', 'Go', 'To'],
  en: ['B', 'KB', 'MB', 'GB', 'TB'],
};

export interface Formatters {
  /** Taille lisible : « 1,2 Mo » ou « 1.2 MB » selon la langue. */
  bytes: (value: number, decimals?: number) => string;
  /** Débit lisible : « 4,5 Mo/s » ou « 4.5 MB/s ». */
  speed: (bytesPerSecond: number) => string;
  /** Durée lisible : « 2 min 30 s ». */
  duration: (seconds: number) => string;
  /** Nombre formaté selon les conventions locales. */
  number: (value: number) => string;
}

/**
 * Construit les formateurs propres à une langue.
 *
 * `Intl.NumberFormat` gère le séparateur décimal et l'espace insécable des milliers :
 * réimplémenter ces règles à la main est une source classique d'affichages fautifs.
 */
export function createFormatters(locale: Locale): Formatters {
  const units = BYTE_UNITS[locale];
  const decimal = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });

  return {
    number: (value) => decimal.format(value),

    bytes: (value, decimals = 1) => {
      if (!Number.isFinite(value) || value < 0) return '—';
      if (value === 0) return `0 ${units[0]}`;

      const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
      const scaled = value / 1024 ** exponent;
      const unit = units[exponent] ?? units[0];

      // Pas de décimale pour les octets seuls : « 512 o » se lit mieux que « 512,0 o ».
      const formatted =
        exponent === 0
          ? integer.format(scaled)
          : new Intl.NumberFormat(locale, {
              maximumFractionDigits: decimals,
            }).format(scaled);

      return `${formatted} ${unit}`;
    },

    speed: (bytesPerSecond) => {
      if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '—';
      return `${createFormatters(locale).bytes(bytesPerSecond)}/s`;
    },

    duration: (seconds) => {
      if (!Number.isFinite(seconds) || seconds < 0) return '—';
      const total = Math.round(seconds);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const secs = total % 60;

      if (hours > 0) {
        return `${integer.format(hours)} h ${String(minutes).padStart(2, '0')} min`;
      }
      if (minutes > 0) {
        return `${integer.format(minutes)} min ${String(secs).padStart(2, '0')} s`;
      }
      return `${integer.format(secs)} s`;
    },
  };
}

/** Formateurs liés à la langue courante. */
export function useFormatters(): Formatters {
  const locale = useLocaleStore((state) => state.locale);
  return useMemo(() => createFormatters(locale), [locale]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Messages transportables
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Message non encore formaté.
 *
 * Permet aux couches métier de signaler une erreur sans connaître la langue de
 * l'utilisateur — et sans figer la traduction au moment où l'erreur est produite.
 */
export type MessageDescriptor =
  | { kind: 'key'; key: MessageKey; params?: MessageParams }
  | { kind: 'plural'; base: PluralBase; count: number; params?: MessageParams }
  /** Texte déjà formé, reçu du serveur ou du pair : on ne peut que l'afficher tel quel. */
  | { kind: 'raw'; text: string };

export const msg = {
  key: (key: MessageKey, params?: MessageParams): MessageDescriptor => ({
    kind: 'key',
    key,
    params,
  }),
  plural: (base: PluralBase, count: number, params?: MessageParams): MessageDescriptor => ({
    kind: 'plural',
    base,
    count,
    params,
  }),
  raw: (text: string): MessageDescriptor => ({ kind: 'raw', text }),
};

/** Met en forme un message transportable dans la langue courante. */
export function formatMessage(translator: Translator, descriptor: MessageDescriptor): string {
  switch (descriptor.kind) {
    case 'key':
      return translator.t(descriptor.key, descriptor.params);
    case 'plural':
      return translator.plural(descriptor.base, descriptor.count, descriptor.params);
    case 'raw':
      return descriptor.text;
  }
}

/** Raccourci : met en forme, ou renvoie `null` si le message est absent. */
export function formatMessageOrNull(
  translator: Translator,
  descriptor: MessageDescriptor | null,
): string | null {
  return descriptor === null ? null : formatMessage(translator, descriptor);
}

// ─────────────────────────────────────────────────────────────────────────────
// Erreurs transportant un message traduisible
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Représentation technique d'un message, destinée aux journaux — jamais à l'écran.
 *
 * Affiche la clé plutôt que le texte : dans une console, `errors.noPicker` situe le
 * problème immédiatement, là où une phrase traduite oblige à remonter à la source.
 */
function describeForDevelopers(descriptor: MessageDescriptor): string {
  switch (descriptor.kind) {
    case 'raw':
      return descriptor.text;
    case 'key':
      return descriptor.key;
    case 'plural':
      return `${descriptor.base}.${descriptor.count === 1 ? 'one' : 'other'}`;
  }
}

/**
 * Erreur dont le message est destiné à l'utilisateur, et donc traduisible.
 *
 * Distingue deux familles d'échecs qu'il ne faut pas confondre :
 *
 * - les **erreurs métier** (« ce navigateur ne peut pas recevoir ce fichier ») méritent
 *   une phrase soignée, dans la langue de l'utilisateur ;
 * - les **erreurs techniques** (« séquence de trame inattendue : reçue 42, attendue 43 »)
 *   ne veulent rien dire pour lui et relèvent du journal de développement.
 *
 * Sans cette distinction, on affiche soit des messages bruts incompréhensibles, soit des
 * formulations vagues qui privent le développeur de l'information de diagnostic.
 */
export class LocalizedError extends Error {
  readonly descriptor: MessageDescriptor;

  constructor(descriptor: MessageDescriptor) {
    super(describeForDevelopers(descriptor));
    this.name = 'LocalizedError';
    this.descriptor = descriptor;
  }
}

/**
 * Convertit une exception en message affichable.
 *
 * Une erreur inconnue devient un texte brut : c'est le bon compromis pour un défaut
 * inattendu — mieux vaut un message technique visible qu'un « une erreur est survenue »
 * qui prive de toute piste.
 */
export function toDescriptor(error: unknown): MessageDescriptor {
  if (error instanceof LocalizedError) return error.descriptor;
  if (error instanceof Error && error.message) return msg.raw(error.message);
  // Une valeur lancée qui n'est pas une `Error` — une chaîne, typiquement — porte
  // souvent le message utile. La réduire à « erreur inconnue » perdrait l'information
  // au moment précis où l'on en a le plus besoin.
  if (typeof error === 'string' && error.trim().length > 0) return msg.raw(error.trim());
  return msg.key('common.unknownError');
}
